import { useState, useRef, useCallback } from 'react'
import legalService from '@api/v1/legalService'

/** Inline source card for case/judge/docket results */
function SourceCard({ source }) {
  if (source.type === 'judge') {
    return (
      <a
        href={source.absoluteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block border border-gray-100 rounded-lg p-3 hover:border-cb-accent/40 hover:bg-cb-light/30 transition-colors"
      >
        <p className="text-sm font-medium text-gray-900">{source.name}</p>
        {source.positions?.[0] && (
          <p className="text-xs text-gray-500 mt-0.5">
            {source.positions[0].court}{source.positions[0].appointer ? ` — Appointed by ${source.positions[0].appointer}` : ''}
          </p>
        )}
        <span className="inline-block mt-1.5 text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">Judge</span>
      </a>
    )
  }

  if (source.type === 'docket') {
    return (
      <a
        href={source.absoluteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block border border-gray-100 rounded-lg p-3 hover:border-cb-accent/40 hover:bg-cb-light/30 transition-colors"
      >
        <p className="text-sm font-medium text-gray-900">{source.caseName}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {source.docketNumber}{source.court ? ` — ${source.court}` : ''}{source.dateFiled ? ` (${source.dateFiled})` : ''}
        </p>
        {source.assignedTo && <p className="text-xs text-gray-500 mt-0.5">Judge: {source.assignedTo}</p>}
        <span className="inline-block mt-1.5 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Docket</span>
      </a>
    )
  }

  // Default: case
  const citations = source.citations?.filter(Boolean).join(', ')
  return (
    <a
      href={source.absoluteUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-gray-100 rounded-lg p-3 hover:border-cb-accent/40 hover:bg-cb-light/30 transition-colors"
    >
      <p className="text-sm font-medium text-gray-900">{source.caseName}</p>
      <p className="text-xs text-gray-500 mt-0.5">
        {citations || source.docketNumber || ''}{source.court ? ` — ${source.court}` : ''}{source.dateFiled ? ` (${source.dateFiled})` : ''}
      </p>
      {source.snippet && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: source.snippet }} />
      )}
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-[10px] font-medium text-cb-accent-text bg-cb-light px-1.5 py-0.5 rounded">Case</span>
        {source.citeCount > 0 && (
          <span className="text-[10px] text-gray-500">{source.citeCount} citations</span>
        )}
      </div>
    </a>
  )
}

/**
 * CaseLawChat — conversational legal research powered by CourtListener.
 * Ask questions about case law, citations, judges, or dockets.
 */
export default function CaseLawChat({ onClose }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Ask me about case law, citations, judges, or dockets. I\'ll search CourtListener to find what you need.' },
  ])
  const [input, setInput] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
  }, [])

  // Build history for multi-turn context (text-only, no sources)
  const getHistory = useCallback(() => {
    return messages
      .filter((m) => m.text)
      .map((m) => ({ role: m.role, text: m.text }))
  }, [messages])

  const handleSubmit = useCallback(async (text) => {
    const trimmed = (text || input).trim()
    if (!trimmed || isProcessing) return

    setInput('')
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }])
    setIsProcessing(true)
    scrollToBottom()

    try {
      const result = await legalService.chat(trimmed, getHistory())
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: result.text || 'I found some results but couldn\'t generate a summary.',
        sources: result.sources || [],
      }])
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: `Sorry, something went wrong. ${err.response?.data?.error || err.message || 'Please try again.'}`,
      }])
    } finally {
      setIsProcessing(false)
      scrollToBottom()
    }
  }, [input, isProcessing, scrollToBottom, getHistory])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-cb-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-900">Case Law Research</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded text-gray-500 hover:text-gray-600 hover:bg-gray-100">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[90%]`}>
              <div className={`rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-cb-navy text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {msg.text}
              </div>
              {msg.sources?.length > 0 && (
                <div className="mt-2 space-y-2">
                  <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide px-1">Sources</p>
                  {msg.sources.slice(0, 6).map((source, j) => (
                    <SourceCard key={source.id || j} source={source} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-500">Searching case law...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 px-3 py-3">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='e.g. "Cases on employment discrimination in the 9th Circuit"'
              rows={1}
              className="w-full resize-none text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-cb-navy/20 focus:border-cb-navy placeholder:text-gray-500"
            />
            <button
              onClick={() => handleSubmit()}
              disabled={!input.trim() || isProcessing}
              className="absolute right-2 bottom-2 p-1 rounded text-cb-navy hover:bg-cb-navy/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-[10px] text-gray-500 mt-1.5 px-1">
          Search U.S. case law, citations, judges, and dockets via CourtListener.
        </p>
      </div>
    </div>
  )
}
